import { useEffect, useState } from 'react';
import api from '../services/api';
import './KioskMode.css';

const KioskMode = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Helper per copiare testo (con fallback per browser non sicuri)
  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      // Fallback per browser senza clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      return success;
    } catch (err) {
      console.error('Errore copia:', err);
      return false;
    }
  };

  // Fetch configurazione corrente
  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/admin/provisioning/qr-config');
      const data = response.data;
      setConfig(data);
    } catch (err) {
      console.error('Errore fetch config:', err);
      if (err.response?.status === 404) {
        setError('Nessuna configurazione trovata. Crea la prima configurazione.');
      } else {
        setError(err.response?.data?.error || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  if (loading) {
    return (
      <div className="kiosk-mode-container">
        <div className="loading">⏳ Caricamento configurazione...</div>
      </div>
    );
  }

  return (
    <div className="kiosk-mode-container">
      <div className="page-header">
        <div>
          <h2>🔐 Kiosk Mode Provisioning</h2>
          <p className="subtitle">
            Setup e configurazione dispositivi kiosk tramite ADB
          </p>
        </div>
        <button onClick={() => setShowInfoModal(true)} className="btn-info">
          ℹ️ Cos'è il Kiosk Mode?
        </button>
      </div>

      {error && !config && (
        <div className="error-box">
          <strong>⚠️ {error}</strong>
        </div>
      )}

      {/* QR Code Section - DISABILITATO (provisioning QR non funzionante) */}
      {/* {qrCodeDataUrl && config && (
        <div className="qr-section">
          <div className="qr-card">
            <h3>📱 QR Code Provisioning Universale</h3>
            <div className="qr-image-container">
              <img src={qrCodeDataUrl} alt="QR Code Provisioning" />
            </div>
            <div className="qr-info">
              <p><strong>Versione APK:</strong> {config.metadata.version}</p>
              <p><strong>Ultimo aggiornamento:</strong> {new Date(config.metadata.updatedAt).toLocaleString('it-IT')}</p>
              {config.metadata.updatedBy && (
                <p><strong>Aggiornato da:</strong> {config.metadata.updatedBy}</p>
              )}
            </div>
            <div className="qr-actions">
              <button onClick={handleDownloadQR} className="btn-primary">
                📥 Download PNG
              </button>
              <button onClick={() => window.print()} className="btn-secondary">
                🖨️ Stampa QR
              </button>
            </div>
            <div className="warning-box">
              <strong>⚠️ QR Code Universale</strong>
              <p>Questo QR è valido per TUTTI i dispositivi SoonTip. Dopo la scansione, il gestore dovrà inserire manualmente il setup code specifico del locale.</p>
            </div>
          </div>
        </div>
      )} */}

      {/* Guida Provisioning Manuale */}
      <div className="guide-section">
        <h3>📋 Guida Setup Dispositivo Kiosk - Procedura Manuale</h3>
        <p className="subtitle">Comandi ADB passo-passo per configurare il dispositivo</p>
        <div className="guide-steps">
          <div className="step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h4>🔄 Factory Reset Tablet</h4>
              <p>Resetta completamente il dispositivo:</p>
              <div className="code-block">
                <code>adb shell recovery --wipe_data</code>
                <button onClick={() => copyToClipboard('adb shell recovery --wipe_data')} className="btn-copy">📋</button>
              </div>
              <p>Oppure manualmente: <strong>Impostazioni → Sistema → Ripristino</strong></p>
              <p className="warning">⚠️ IMPORTANTE: Dopo il reset, NON configurare Google Account!</p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h4>🔌 Verifica Connessione ADB</h4>
              <p>Collega il tablet via USB e verifica la connessione:</p>
              <div className="code-block">
                <code>adb devices</code>
                <button onClick={() => copyToClipboard('adb devices')} className="btn-copy">📋</button>
              </div>
              <p>Dovresti vedere: <code>XXXXXXXX    device</code></p>
              <p className="note">Se vedi "unauthorized", autorizza il debug dal popup sul tablet</p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h4>📥 Installa APK Kiosk</h4>
              <p>Installa l'applicazione SoonTip Kiosk sul dispositivo:</p>
              <div className="code-block">
                <code>adb install -r app-kiosk-release.apk</code>
                <button onClick={() => copyToClipboard('adb install -r app-kiosk-release.apk')} className="btn-copy">📋</button>
              </div>
              <p>Il flag <code>-r</code> reinstalla se già presente</p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">4</div>
            <div className="step-content">
              <h4>🔓 Concedi Permessi Runtime</h4>
              <p>Pre-concedi tutti i permessi necessari per evitare popup durante l'avvio:</p>

              <div className="sub-step">
                <p><strong>Permesso GPS (richiesto da Stripe Terminal):</strong></p>
                <div className="code-block">
                  <code>adb shell pm grant com.soontip.kiosk android.permission.ACCESS_FINE_LOCATION</code>
                  <button onClick={() => copyToClipboard('adb shell pm grant com.soontip.kiosk android.permission.ACCESS_FINE_LOCATION')} className="btn-copy">📋</button>
                </div>
              </div>

              <div className="sub-step">
                <p><strong>Permessi Bluetooth (Android 12+):</strong></p>
                <div className="code-block">
                  <code>adb shell pm grant com.soontip.kiosk android.permission.BLUETOOTH_CONNECT</code>
                  <button onClick={() => copyToClipboard('adb shell pm grant com.soontip.kiosk android.permission.BLUETOOTH_CONNECT')} className="btn-copy">📋</button>
                </div>
                <div className="code-block">
                  <code>adb shell pm grant com.soontip.kiosk android.permission.BLUETOOTH_SCAN</code>
                  <button onClick={() => copyToClipboard('adb shell pm grant com.soontip.kiosk android.permission.BLUETOOTH_SCAN')} className="btn-copy">📋</button>
                </div>
              </div>

              <div className="sub-step">
                <p><strong>Permesso Camera:</strong></p>
                <div className="code-block">
                  <code>adb shell pm grant com.soontip.kiosk android.permission.CAMERA</code>
                  <button onClick={() => copyToClipboard('adb shell pm grant com.soontip.kiosk android.permission.CAMERA')} className="btn-copy">📋</button>
                </div>
              </div>

              <p className="note">ℹ️ I comandi Bluetooth potrebbero fallire su Android 11 o precedenti (è normale)</p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">5</div>
            <div className="step-content">
              <h4>🔐 Imposta Device Owner (Kiosk Mode)</h4>
              <p>Configura l'app come Device Owner per bloccarla in primo piano:</p>
              <div className="code-block">
                <code>adb shell dpm set-device-owner com.soontip.kiosk/.SoonTipDeviceAdminReceiver</code>
                <button onClick={() => copyToClipboard('adb shell dpm set-device-owner com.soontip.kiosk/.SoonTipDeviceAdminReceiver')} className="btn-copy">📋</button>
              </div>
              <p className="warning">⚠️ Questo comando è IRREVERSIBILE senza factory reset!</p>
              <p>Successo se vedi: <code>Success: Device owner set to package com.soontip.kiosk</code></p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">6</div>
            <div className="step-content">
              <h4>🔋 Disabilita Battery Optimization</h4>
              <p>Impedisci che Android metta l'app in sleep:</p>
              <div className="code-block">
                <code>adb shell dumpsys deviceidle whitelist +com.soontip.kiosk</code>
                <button onClick={() => copyToClipboard('adb shell dumpsys deviceidle whitelist +com.soontip.kiosk')} className="btn-copy">📋</button>
              </div>
              <p>Garantisce che lo schermo resti sempre acceso e l'app sempre attiva</p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">7</div>
            <div className="step-content">
              <h4>⚙️ Ottimizzazioni Sistema</h4>
              <p>Configura impostazioni ottimali per kiosk mode:</p>

              <div className="sub-step">
                <p><strong>Abilita WiFi:</strong></p>
                <div className="code-block">
                  <code>adb shell svc wifi enable</code>
                  <button onClick={() => copyToClipboard('adb shell svc wifi enable')} className="btn-copy">📋</button>
                </div>
              </div>

              <div className="sub-step">
                <p><strong>WiFi Always-On (connessione persistente):</strong></p>
                <div className="code-block">
                  <code>adb shell settings put global wifi_sleep_policy 2</code>
                  <button onClick={() => copyToClipboard('adb shell settings put global wifi_sleep_policy 2')} className="btn-copy">📋</button>
                </div>
              </div>

              <div className="sub-step">
                <p><strong>GPS High Accuracy (richiesto da Stripe Terminal):</strong></p>
                <div className="code-block">
                  <code>adb shell settings put secure location_mode 3</code>
                  <button onClick={() => copyToClipboard('adb shell settings put secure location_mode 3')} className="btn-copy">📋</button>
                </div>
              </div>

              <div className="sub-step">
                <p><strong>Schermo sempre acceso durante carica:</strong></p>
                <div className="code-block">
                  <code>adb shell settings put global stay_on_while_plugged_in 7</code>
                  <button onClick={() => copyToClipboard('adb shell settings put global stay_on_while_plugged_in 7')} className="btn-copy">📋</button>
                </div>
                <p className="note">ℹ️ Il valore 7 = USB + AC + Wireless (qualsiasi tipo di alimentazione)</p>
              </div>

              <div className="sub-step">
                <p><strong>Disabilita Lock Screen (rimuove swipe e PIN):</strong></p>
                <div className="code-block">
                  <code>adb shell locksettings set-disabled true</code>
                  <button onClick={() => copyToClipboard('adb shell locksettings set-disabled true')} className="btn-copy">📋</button>
                </div>
                <p className="note">ℹ️ Elimina completamente la schermata di blocco (swipe/PIN/pattern)</p>
              </div>
            </div>
          </div>

          <div className="step">
            <div className="step-number">8</div>
            <div className="step-content">
              <h4>🚀 Avvia Applicazione</h4>
              <p>Lancia SoonTip Kiosk:</p>
              <div className="code-block">
                <code>adb shell am start -n com.soontip.kiosk/.MainActivity</code>
                <button onClick={() => copyToClipboard('adb shell am start -n com.soontip.kiosk/.MainActivity')} className="btn-copy">📋</button>
              </div>
              <p>L'app si avvierà automaticamente in kiosk mode bloccata in primo piano</p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">9</div>
            <div className="step-content">
              <h4>✅ Verifica Configurazione</h4>
              <p>Controlla che il Device Owner sia stato impostato correttamente:</p>
              <div className="code-block">
                <code>adb shell dumpsys device_policy | grep -A 5 "Device Owner"</code>
                <button onClick={() => copyToClipboard('adb shell dumpsys device_policy | grep -A 5 "Device Owner"')} className="btn-copy">📋</button>
              </div>
              <p>Dovresti vedere <code>admin=DeviceAdminInfo...SoonTipDeviceAdminReceiver</code></p>
            </div>
          </div>
        </div>

        {/* Automazione con Script */}
        <div className="automation-section">
          <h4>⚡ Automatizza Tutto con lo Script</h4>
          <p>Tutti i passaggi sopra possono essere eseguiti automaticamente con un singolo script bash.</p>

          <div className="automation-steps">
            <p><strong>Prerequisiti:</strong></p>
            <ul>
              <li>Dispositivo Android in <strong>factory reset</strong> (SENZA Google Account configurato)</li>
              <li><strong>USB debugging</strong> abilitato nelle Opzioni Sviluppatore</li>
              <li>Dispositivo <strong>connesso via USB</strong> e autorizzato per ADB</li>
              <li>File APK kiosk (es. <code>app-kiosk-release.apk</code>, <code>soontip-kiosk-v1.0.1.apk</code>, ecc.)</li>
              <li>File <code>setup-kiosk-device.sh</code></li>
            </ul>

            <p><strong>Procedura:</strong></p>
            <ol>
              <li>Posiziona entrambi i file nella stessa cartella</li>
              <li>Apri un terminale bash in quella cartella</li>
              <li>Esegui lo script:</li>
            </ol>

            <div className="code-block highlight">
              <code>./setup-kiosk-device.sh</code>
              <button onClick={() => copyToClipboard('./setup-kiosk-device.sh')} className="btn-copy"></button>
            </div>

            <p className="note" style={{ marginTop: '12px' }}>
              💡 Se ricevi un errore di permessi, rendi prima eseguibile lo script con: <code>chmod +x setup-kiosk-device.sh</code>
            </p>
          </div>

          <p className="note">
            ℹ️ Lo script rileva automaticamente il primo file <code>.apk</code> nella cartella,
            esegue tutti i 7 step con progresso visuale e richiede conferma prima di impostare il Device Owner.
            <strong>Consigliato per setup frequenti!</strong>
          </p>
        </div>

        {/* Configurazione APK - DISABILITATA (gestita tramite script backend)
        <div className="config-section">
          <h3>🔧 Configurazione APK</h3>
          <p>Gestisci la configurazione del file APK per il provisioning:</p>

          {!editing ? (
            <div className="config-display">
              {config?.metadata && (
                <div className="config-info">
                  <p><strong>APK URL:</strong> {config.metadata.apkUrl}</p>
                  <p><strong>Checksum:</strong> <code>{config.metadata.apkChecksum}</code></p>
                  <p><strong>Versione:</strong> {config.metadata.version}</p>
                  <p className="note">
                    ℹ️ Il checksum è l'impronta digitale SHA-256 del certificato di firma dell'APK (44 caratteri base64).
                  </p>
                </div>
              )}
              <button onClick={() => {
                setEditing(true);
                setFormData({
                  apkUrl: config?.metadata?.apkUrl || '',
                  apkChecksum: config?.metadata?.apkChecksum || '',
                  apkVersion: config?.metadata?.version || ''
                });
              }} className="btn-edit">
                ✏️ Modifica Configurazione
              </button>
            </div>
          ) : (
            <form onSubmit={handleUpdateConfig} className="config-form">
              <div className="form-group">
                <label>APK URL:</label>
                <input
                  type="url"
                  value={formData.apkUrl}
                  onChange={(e) => setFormData({...formData, apkUrl: e.target.value})}
                  placeholder="https://esempio.com/app-kiosk-release.apk"
                  required
                />
              </div>

              <div className="form-group">
                <label>Checksum (SHA256 base64, 44 caratteri):</label>
                <input
                  type="text"
                  value={formData.apkChecksum}
                  onChange={(e) => setFormData({...formData, apkChecksum: e.target.value})}
                  placeholder="Q0sPrs6aSDISoxqzI+MoZwdYz9od8CekpYYEmjyk30Y="
                  pattern="^[A-Za-z0-9+/]{43}=$"
                  title="Deve essere SHA256 in base64 (44 caratteri, termina con =)"
                  required
                />
                <p className="hint">
                  💡 Usa <code>keytool -printcert -jarfile app.apk</code> per estrarre il checksum del certificato
                </p>
              </div>

              <div className="form-group">
                <label>Versione (semver):</label>
                <input
                  type="text"
                  value={formData.apkVersion}
                  onChange={(e) => setFormData({...formData, apkVersion: e.target.value})}
                  placeholder="1.0.0"
                  pattern="^\d+\.\d+\.\d+$"
                  title="Formato: X.Y.Z (es. 1.0.0)"
                  required
                />
              </div>

              <div className="form-actions">
                <button type="submit" disabled={saving} className="btn-save">
                  {saving ? '⏳ Salvando...' : '💾 Salva Configurazione'}
                </button>
                <button type="button" onClick={() => {
                  setEditing(false);
                  setFormData({ apkUrl: '', apkChecksum: '', apkVersion: '' });
                }} className="btn-cancel">
                  ❌ Annulla
                </button>
              </div>
            </form>
          )}
        </div>
        */}
      </div>

      {/* Modal Info Kiosk Mode */}
      {showInfoModal && (
        <div className="modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔐 Cos'è il Kiosk Mode?</h2>
              <button className="modal-close" onClick={() => setShowInfoModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="info-section">
                <h3>📱 Definizione</h3>
                <p>
                  Il <strong>Kiosk Mode</strong> trasforma il tablet in un dispositivo dedicato esclusivamente all'app SoonTip,
                  bloccando completamente l'accesso a tutte le altre funzionalità Android. Funziona come un bancomat o un totem
                  informativo: il dispositivo svolge solo una funzione specifica e non può essere utilizzato per altro.
                </p>
              </div>

              <div className="info-section">
                <h3>🎯 Perché è necessario?</h3>
                <ul className="benefits-list">
                  <li>
                    <strong>🔒 Sicurezza Totale:</strong> Gli utenti non possono uscire dall'app, disinstallarla,
                    accedere alle impostazioni o utilizzare altre applicazioni
                  </li>
                  <li>
                    <strong>⚡ Affidabilità 24/7:</strong> L'app rimane sempre attiva e funzionante,
                    anche dopo riavvii del dispositivo o interruzioni di corrente
                  </li>
                  <li>
                    <strong>💡 Disponibilità Continua:</strong> Il dispositivo non va mai in sleep e
                    lo schermo resta sempre acceso, pronto per accettare pagamenti in qualsiasi momento
                  </li>
                </ul>
              </div>

              <div className="info-section">
                <h3>🛡️ Protezioni Attivate</h3>
                <div className="protection-grid">

                  <div className="protection-card">
                    <div className="protection-icon">🔐</div>
                    <h4>Blocco Totale App</h4>
                    <p>
                      L'app viene "incollata" allo schermo. Gli utenti non possono premere il tasto Home,
                      aprire altre app, accedere alle notifiche o alle impostazioni di sistema.
                    </p>
                    <span className="tech-name">Lock Task Mode</span>
                  </div>

                  <div className="protection-card">
                    <div className="protection-icon">💡</div>
                    <h4>Schermo Sempre Acceso</h4>
                    <p>
                      Il dispositivo mantiene lo schermo sempre acceso e luminoso, impedendo qualsiasi
                      spegnimento automatico. Garantisce che il kiosk sia sempre visibile e pronto all'uso.
                    </p>
                    <span className="tech-name">Triple Screen Protection</span>
                  </div>

                  <div className="protection-card">
                    <div className="protection-icon">🖥️</div>
                    <h4>Schermo Intero</h4>
                    <p>
                      Nasconde completamente la barra di stato (orologio, batteria, WiFi) e la barra di
                      navigazione (tasti Home, Back), massimizzando lo spazio per l'interfaccia dell'app.
                    </p>
                    <span className="tech-name">Immersive Mode</span>
                  </div>

                  <div className="protection-card">
                    <div className="protection-icon">🚀</div>
                    <h4>Avvio Automatico</h4>
                    <p>
                      Quando il tablet si riavvia (per aggiornamenti o interruzioni di corrente),
                      l'app SoonTip si riapre automaticamente e si riconfigura in modalità kiosk.
                    </p>
                    <span className="tech-name">Boot Auto-Start</span>
                  </div>

                  <div className="protection-card">
                    <div className="protection-icon">💓</div>
                    <h4>Processo Sempre Vivo</h4>
                    <p>
                      Un servizio speciale mantiene l'app sempre in esecuzione, impedendo al sistema
                      Android di chiuderla per liberare memoria. Garantisce funzionamento continuo 24/7.
                    </p>
                    <span className="tech-name">Foreground Service</span>
                  </div>

                  <div className="protection-card">
                    <div className="protection-icon">🔋</div>
                    <h4>Nessun Risparmio Energetico</h4>
                    <p>
                      Disabilita tutte le ottimizzazioni batteria che potrebbero mettere l'app in
                      modalità risparmio energetico, garantendo comunicazione costante con il server.
                    </p>
                    <span className="tech-name">Battery Optimization Disabled</span>
                  </div>

                  <div className="protection-card">
                    <div className="protection-icon">👑</div>
                    <h4>Controllo Dispositivo</h4>
                    <p>
                      L'app ottiene il controllo completo del tablet come "super-amministratore",
                      impedendo la disinstallazione e garantendo che nessun'altra app possa interferire.
                    </p>
                    <span className="tech-name">Device Owner Mode</span>
                  </div>

                  <div className="protection-card">
                    <div className="protection-icon">🔓</div>
                    <h4>Uscita Solo per Admin</h4>
                    <p>
                      Solo gli amministratori con PIN dedicato possono uscire dalla modalità kiosk
                      per manutenzione o configurazione. Gli utenti comuni restano sempre bloccati.
                    </p>
                    <span className="tech-name">Admin PIN Protection</span>
                  </div>

                </div>
              </div>

              <div className="info-section">
                <h3>📊 Risultato Finale</h3>
                <div className="final-result">
                  <p>
                    Con tutte queste protezioni attive, il tablet diventa un <strong>terminale di pagamento professionale</strong>:
                  </p>
                  <ul className="result-list">
                    <li>✅ Impossibile uscire dall'app o disinstallarla</li>
                    <li>✅ Schermo sempre acceso, mai in sleep</li>
                    <li>✅ Riavvio automatico dopo interruzioni corrente</li>
                    <li>✅ Processo sempre attivo, nessuna interruzione servizio</li>
                    <li>✅ Comunicazione costante con server e lettore Stripe</li>
                    <li>✅ Interfaccia pulita senza distrazioni di sistema</li>
                    <li>✅ Sicurezza massima contro uso improprio</li>
                  </ul>
                </div>
              </div>

              <div className="info-section info-warning">
                <h3>⚠️ Importante da Sapere</h3>
                <p>
                  <strong>Il Kiosk Mode è irreversibile senza factory reset.</strong> Una volta attivato,
                  è volutamente difficile da rimuovere per garantire la sicurezza. Solo un factory reset
                  completo del dispositivo può disattivarlo. Questo è un comportamento voluto per impedire
                  manomissioni del sistema da parte di utenti non autorizzati.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-primary" onClick={() => setShowInfoModal(false)}>
                Ho capito
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KioskMode;
